import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { bannerSlides } from '@/mocks/home';

function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollY, setScrollY] = useState(0);

  // Parallax on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        setScrollY(window.scrollY * 0.35);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preload adjacent images
  useEffect(() => {
    const next = (current + 1) % bannerSlides.length;
    const prev = (current - 1 + bannerSlides.length) % bannerSlides.length;
    const toLoad = [next, prev].filter((i) => !loadedImages.has(i));
    if (toLoad.length === 0) return;

    toLoad.forEach((idx) => {
      const img = new Image();
      img.onload = () => setLoadedImages((prev) => new Set([...prev, idx]));
      img.src = bannerSlides[idx].bgImage;
      // Preload product image too
      const product = new Image();
      product.src = bannerSlides[idx].productImage;
    });
  }, [current, loadedImages]);

  const autoNext = useCallback(() => {
    setCurrent((prev) => (prev + 1) % bannerSlides.length);
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning || index === current) return;
      setIsTransitioning(true);
      setCurrent(index);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(autoNext, 6000);
      setTimeout(() => setIsTransitioning(false), 650);
    },
    [isTransitioning, current, autoNext],
  );

  const next = useCallback(() => goTo((current + 1) % bannerSlides.length), [current, goTo]);
  const prev = useCallback(
    () => goTo((current - 1 + bannerSlides.length) % bannerSlides.length),
    [current, goTo],
  );

  useEffect(() => {
    timerRef.current = setInterval(autoNext, 6000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoNext]);

  const slide = bannerSlides[current];

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen min-h-[620px] md:min-h-[750px] overflow-hidden"
    >
      {/* Background image layer */}
      <div className="absolute inset-0 z-0">
        {bannerSlides.map((s, i) => {
          const isActive = i === current;
          const isPrev =
            i === (current - 1 + bannerSlides.length) % bannerSlides.length;
          return (
            <img
              key={s.id}
              src={loadedImages.has(i) || isActive ? s.bgImage : ''}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ease-in-out"
              style={{
                opacity: isActive ? 1 : isPrev ? 0 : 0,
                transform: isActive
                  ? `translateY(${scrollY * 0.15}px) scale(1)`
                  : 'scale(1.05)',
                zIndex: isActive ? 1 : 0,
              }}
            />
          );
        })}
      </div>

      {/* Content layer */}
      <div className="relative z-20 h-full flex flex-col items-center justify-center px-4 md:px-8">
        {/* Floating decorative blur orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute w-[500px] h-[500px] rounded-full opacity-[0.07] blur-[120px] transition-all duration-1000"
            style={{
              backgroundColor: 'oklch(var(--primary-400))',
              top: '10%',
              left: `${30 + scrollY * 0.05}%`,
            }}
          />
          <div
            className="absolute w-[400px] h-[400px] rounded-full opacity-[0.05] blur-[100px] transition-all duration-1000"
            style={{
              backgroundColor: 'oklch(var(--accent-400))',
              bottom: '15%',
              right: `${25 - scrollY * 0.03}%`,
            }}
          />
        </div>

        <div className="w-full max-w-6xl mx-auto relative">
          {/* Fade transition wrapper */}
          <div
            key={current}
            className="transition-all duration-600 ease-out flex flex-col md:flex-row items-center md:items-center gap-8 md:gap-12"
            style={{
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'translateY(24px)' : 'translateY(0)',
              filter: isTransitioning ? 'blur(4px)' : 'blur(0)',
            }}
          >
            {/* Left: copy */}
            <div className="flex-1 text-center md:text-left">
              {/* Tag pill */}
              <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-xs md:text-sm font-medium text-foreground-700 border border-foreground-900/15 rounded-full backdrop-blur-md bg-white/40">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                {slide.tag}
              </span>

              {/* Main title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary-600 tracking-tight leading-[1.1] mb-4">
                {slide.title}
              </h1>

              {/* Subtitle */}
              <p className="text-lg md:text-2xl text-foreground-800 font-light mb-3">
                {slide.subtitle}
              </p>

              {/* Description */}
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto md:mx-0 mb-10 leading-relaxed">
                {slide.description}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3.5">
                <Link
                  to={slide.ctaLink}
                  className="group px-8 py-3.5 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-full hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-[0_0_30px_rgba(255,255,255,0.15)]"
                >
                  {slide.ctaText}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-0.5 inline-block transition-transform"></i>
                </Link>
                <a
                  href={slide.ctaSecondaryLink}
                  className="px-8 py-3.5 bg-white/60 backdrop-blur-lg text-foreground-800 text-sm md:text-base font-semibold rounded-full hover:bg-white/80 border border-foreground-900/15 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {slide.ctaSecondary}
                </a>
              </div>
            </div>

            {/* Right: product image */}
            <div className="flex-1 flex items-center justify-center md:justify-end">
              <img
                src={slide.productImage}
                alt={slide.title}
                className="w-full max-w-md lg:max-w-lg h-auto object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
              />
            </div>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-28 md:bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-20">
          {bannerSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all duration-400 rounded-full cursor-pointer ${
                i === current
                  ? 'w-8 h-2 bg-foreground-900/40'
                  : 'w-2 h-2 bg-foreground-900/20 hover:bg-foreground-900/35'
              }`}
              aria-label={`切换到第 ${i + 1} 张`}
            />
          ))}
        </div>

        {/* Scroll-down indicator */}
        {/* <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-900/45">
              向下探索
            </span>
            <i className="ri-arrow-down-s-line text-foreground-900/50 text-sm"></i>
          </div>
        </div> */}
      </div>

      {/* Arrow navigation */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/50 hover:bg-white/70 backdrop-blur-md text-foreground-800 border border-foreground-900/10 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/50 hover:bg-white/70 backdrop-blur-md text-foreground-800 border border-foreground-900/10 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}

export default HeroBanner;